// client/src/components/CategoryBar.js
// Secondary navigation row under the main navbar:
//   [Menu] [New Arrivals] [Specials] ............ [Live Auctions]
// "Menu" opens a two-column panel: categories on the left, the hovered
// category's subcategories on the right.
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Button, Popover, List, ListItemButton, ListItemText, Typography, Stack, Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GavelIcon from '@mui/icons-material/Gavel';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useCategories } from '../context/CategoryContext';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import { getCategoryIcon } from '../constants/categoryIcons';

const CategoryBar = () => {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const { isEnabled } = useContext(FeatureFlagContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const activeCategory = categories.find((c) => c.id === activeId) || categories[0];

  const closeMenu = () => setAnchorEl(null);

  const goTo = (params) => {
    closeMenu();
    navigate(`/?${new URLSearchParams(params).toString()}`);
  };

  const linkSx = { color: 'text.primary', fontWeight: 600, px: 1.5, whiteSpace: 'nowrap' };

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Stack direction="row" alignItems="center" sx={{ px: { xs: 1, md: 3 }, minHeight: 44, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
        {/* Equal-width side columns keep the middle group truly centred. */}
        <Box sx={{ flex: 1 }} />

        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            color="inherit"
            startIcon={<MenuIcon fontSize="small" />}
            onClick={(e) => { setActiveId(categories[0]?.id ?? null); setAnchorEl(e.currentTarget); }}
            sx={linkSx}
          >
            Categories
          </Button>
          <Button size="small" color="inherit" onClick={() => goTo({ sort: 'newest' })} sx={linkSx}>
            New Arrivals
          </Button>
          <Button size="small" color="inherit" onClick={() => goTo({ sale: 'true' })} sx={{ ...linkSx, color: 'error.main' }}>
            Specials
          </Button>
          {isEnabled('auctions') && (
            <Button
              component={Link}
              to="/auctions"
              size="small"
              startIcon={<GavelIcon fontSize="small" />}
              sx={{ ...linkSx, fontWeight: 700, color: 'secondary.main' }}
            >
              Live Auctions
            </Button>
          )}
        </Stack>

        <Box sx={{ flex: 1 }} />
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 3, mt: 0.5, overflow: 'hidden' } } }}
      >
        {categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>No categories yet.</Typography>
        ) : (
          <Stack direction="row" sx={{ width: 560, maxWidth: '90vw', maxHeight: '70vh' }}>
            <Box sx={{ width: '45%', py: 1, overflowY: 'auto', borderRight: 1, borderColor: 'divider' }}>
              <List dense disablePadding>
                {categories.map((category) => {
                  const Icon = getCategoryIcon(category.icon_key);
                  return (
                    <ListItemButton
                      key={category.id}
                      selected={activeCategory?.id === category.id}
                      onMouseEnter={() => setActiveId(category.id)}
                      onFocus={() => setActiveId(category.id)}
                      onClick={() => goTo({ category: category.name })}
                    >
                      <Icon color="primary" fontSize="small" sx={{ mr: 1.5 }} />
                      <ListItemText primary={category.name} />
                      {category.children.length > 0 && <ChevronRightIcon fontSize="small" color="disabled" />}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
            <Box sx={{ width: '55%', py: 1.5, px: 2, overflowY: 'auto' }}>
              <Typography variant="overline" color="text.secondary">{activeCategory?.name}</Typography>
              <Divider sx={{ my: 1 }} />
              <List dense disablePadding>
                <ListItemButton onClick={() => goTo({ category: activeCategory?.name })} sx={{ borderRadius: 2 }}>
                  <ListItemText primary={`All ${activeCategory?.name}`} primaryTypographyProps={{ fontWeight: 700 }} />
                </ListItemButton>
                {activeCategory?.children.map((sub) => (
                  <ListItemButton
                    key={sub.id}
                    onClick={() => goTo({ category: activeCategory.name, subcategory: sub.name })}
                    sx={{ borderRadius: 2 }}
                  >
                    <ListItemText primary={sub.name} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Stack>
        )}
      </Popover>
    </Box>
  );
};

export default CategoryBar;
